/**
 * @module utils/order
 */
import React from 'react'
import { OrderTypeIcons, TransportTypeIcons, InvoiceIcons, OrderAssessmentIcons, ElementIcons } from '../shared/elements/icons'

/**
 * @description Makes the order type image
 * @return {string} Returns string
 */
export function makeOrderTypeImage (id, white) {
  let image = ''
  switch (id) {
    case 1:
      image = 'Insurance-black'
      break
    case 2:
      image = 'Salescar-black'
      break
    case 3:
      image = 'Garanteed-black'
      break
    case 5:
      image = 'Complaint-black'
      break
    case 6:
      image = 'Private_c-black'
      break
    default:
      image = 'Other-black'
      break
  }
  if (white) image = image.replace('black', 'white')
  return '/images/svgs/ordrer/' + image + '.svg'
}

/**
 * @description Makes the order type icon
 * @return {object} Returns object
 */
export function makeOrderTypeIcon (orderType, white) {
  let icon
  switch (orderType) {
    case 1:
      icon = white ? OrderTypeIcons.Insurance_White : OrderTypeIcons.Insurance_Black
      break
    case 2:
      icon = white ? OrderTypeIcons.Salescar_White : OrderTypeIcons.Salescar_Black
      break
    case 3:
      icon = white ? OrderTypeIcons.Garanteed_White : OrderTypeIcons.Garanteed_Black
      break
    case 5:
      icon = white ? OrderTypeIcons.Complaint_White : OrderTypeIcons.Complaint_Black
      break
    case 6:
      icon = white ? OrderTypeIcons.Private_White : OrderTypeIcons.Private_Black
      break
    default:
      icon = white ? OrderTypeIcons.Other_White : OrderTypeIcons.Other_Black
      break
  }
  return icon
}

/**
 * @description Makes the transport type icon
 * @return {object} Returns object
 */
export function makeTransportIcon (id) {
  let icon
  switch (id) {
    case 1:
      icon = TransportTypeIcons.Can_Drive
      break
    case 2:
      icon = TransportTypeIcons.Loose_Part
      break
    default:
      icon = TransportTypeIcons.Transport
      break
  }
  return icon
}

/**
 * @description Makes the order invoice icon
 * @return {object} Returns object
 */
export function makeOrderInvoiceIcon (val) {
  let icon
  switch (val) {
    case 0:
      icon = InvoiceIcons.Sent_To_Invoice
      break
    case 1:
      icon = InvoiceIcons.Marked_As_Invoice
      break
    case 2:
      icon = InvoiceIcons.Sent_And_Marked
      break
  }
  return icon
}

/**
 * @description Makes the order assessment icon
 * @return {object} Returns object
 */
export function makeOrderAssessmentIcon (title, complete) {
  let icon
  switch (title) {
    case 14983: // Extent
      icon = OrderAssessmentIcons.Extent
      break
    case 2716: // Awaiting, Price change sent
      icon = OrderAssessmentIcons.Invoice_Awaiting
      break
    case 2714: // Approved
    case 2715: // Rejected
      icon = OrderAssessmentIcons.Invoice_Approved_Rejected
      break
    case 936: // Main report
    case 14963:
      icon = OrderAssessmentIcons.Main_Report_Loaded
      break
    case 937: // Additional report
    case 14916:
      icon = OrderAssessmentIcons.Additional_Report
      break
    case 1037: // After evaluation
    case 14962:
      icon = OrderAssessmentIcons.After_Evaluation
      break
    case 1396: // Addon price
      icon = OrderAssessmentIcons.Addon_Price
      break
    case 13363: // Estimation
      if (complete) {
        icon = OrderAssessmentIcons.Estimation_Complete
      } else {
        icon = OrderAssessmentIcons.Estimation
      }
      break
    case 159: // Workcard
      icon = OrderAssessmentIcons.Workcard
      break
    case 1139: // Ready for invoice
      icon = OrderAssessmentIcons.Ready_Invoice
      break
    case 13042: // Sent to invoice
    case 15322:
    case 15267:
    case 14959: // Resent to invoice
      icon = OrderAssessmentIcons.Sent_To_Invoice
      break
    case 1136: // Invoiced
      icon = OrderAssessmentIcons.Invoiced
      break
    case 14958: // Crediting
      icon = OrderAssessmentIcons.Crediting
      break
    case 14960: // Credit note
      icon = OrderAssessmentIcons.Credit_Note
      break
    case 14961: // Invoice
      icon = OrderAssessmentIcons.Invoice
      break
    case 14984: // Addon
    case 1275: // Addon changed
      icon = OrderAssessmentIcons.Addon
      break
    default:
      icon = ElementIcons.Info_Circle
      break
  }
  return icon
}

export function makeOrderPageTitleObject (order) {
  let result = { icon: null, title: null }
  if (typeof order.Id === 'undefined') return result

  let title = ''
  result.icon = makeOrderTypeIcon(order.OrderType, true)

  // RegNumber
  if (order.RegNumber) {
    title = order.RegNumber
  }

  // Model
  if (order.Model) {
    let temp = ''
    if (order.RegNumber) {
      temp += ', '
    }
    temp += order.Model
    title += temp
  }

  // Title empty
  if (title === '') {
    title = order.Id + ''
  }
  result.title = title
  return result
}

/**
 * @description Makes the order title
 * @param {Object} order Order object
 * @return {string} Returns string
 */
export function makeOrderTitle (order, t, enumKey, onlyTitle, white, includePrint) {
  if (typeof order.Id === 'undefined') return ''

  let title = ''
  let titleReg = ''
  let titleModel = ''

  let image = makeOrderTypeImage(order.OrderType, white)

  let segmented = (!onlyTitle && !includePrint)

  // RegNumber
  if (order.RegNumber) {
    if (segmented) {
      titleReg = <span>{order.RegNumber}</span>
    } else {
      title = order.RegNumber
    }
  }

  // Model
  if (order.Model) {
    let temp = ''
    if (order.RegNumber) {
      temp += ', '
    }
    temp += order.Model
    if (segmented) {
      titleModel = <span>{temp}</span>
    } else {
      title += temp
    }
  }

  if (segmented) {
    title = <div>{titleReg}{titleModel}</div>
  }

  // Title empty
  if (title === '') {
    title += order.Id
  }

  if (onlyTitle) return title

  if (includePrint) {
    return ([
      <div className='orderIdentification print' key='1'>
        <div>
          <span className='hint--right' aria-label={t(enumKey('OrderTypes', order.OrderType))}><img src={makeOrderTypeImage(order.OrderType, false)} /></span>
        </div>
        { title }
      </div>,
      <div className='orderIdentification' key='2'>
        <div>
          <span className='hint--right' aria-label={t(enumKey('OrderTypes', order.OrderType))}><img src={image} /></span>
        </div>
        { title }
      </div>
    ])
  }

  return (
    <div className='orderIdentification'>
      <div>
        <span className='hint--right' aria-label={t(enumKey('OrderTypes', order.OrderType))}><img src={image} /></span>
      </div>
      { title }
    </div>
  )
}

/**
 * @description Makes the step title
 * @return {string} Returns string
 */
export function getStepName (id) {
  switch (parseInt(id, 10)) {
    case 1:
      return 164
    case 2:
      return 96
    case 3:
      return 160
    case 4:
      return 158
    case 5:
      return 161
    case 6:
      return 162
    case 7:
      return 163
    case 8:
      return 23
    case 101:
      return 617
    case 102:
      return 618
    case 103:
      return 619
    case 104:
      return 162
    case 105:
      return 161
    default:
      return ''
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/order.js